import { useState, type ReactNode } from 'react';
import { Button, Modal, type OverlayTriggerState } from '@heroui/react';
import { AsyncButton, type AsyncButtonProps } from './async-button';

interface ConfirmDialogProps {
  state: OverlayTriggerState;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  confirmVariant?: AsyncButtonProps['variant'];
  confirmDisabled?: boolean;
  extraAction?: {
    label: string;
    variant?: AsyncButtonProps['variant'];
    onPress: () => Promise<void>;
  };
  onConfirm: () => Promise<void>;
}

export function ConfirmDialog({
  state,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancelar',
  confirmVariant = 'primary',
  confirmDisabled = false,
  extraAction,
  onConfirm,
}: ConfirmDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
      state.close();
    } finally {
      setSubmitting(false);
    }
  };

  const handleExtra = async () => {
    if (!extraAction) return;
    setSubmitting(true);
    try {
      await extraAction.onPress();
      state.close();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal state={state}>
      <Modal.Backdrop isDismissable>
        <Modal.Container placement="center">
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>{title}</Modal.Heading>
              <Modal.CloseTrigger />
            </Modal.Header>
            <Modal.Body>{message}</Modal.Body>
            <Modal.Footer className="flex justify-end gap-2">
              <Button variant="secondary" isDisabled={submitting} onPress={() => state.close()}>
                {cancelLabel}
              </Button>
              {extraAction ? (
                <AsyncButton
                  variant={extraAction.variant ?? 'danger'}
                  isPending={submitting}
                  isDisabled={confirmDisabled}
                  pendingLabel={extraAction.label}
                  onPress={handleExtra}
                >
                  {extraAction.label}
                </AsyncButton>
              ) : null}
              <AsyncButton
                variant={confirmVariant}
                isPending={submitting}
                isDisabled={confirmDisabled}
                pendingLabel={confirmLabel}
                onPress={handleConfirm}
              >
                {confirmLabel}
              </AsyncButton>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
